import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Calendar,
  FileText,
  DownloadSimple,
  Path,
  Speedometer,
  Clock,
  Car,
  Warning,
  Circle,
  CaretDown,
} from '@phosphor-icons/react';
import { reportsAPI, alarmsAPI, devicesAPI } from '@/services/api';
import { format, subDays } from 'date-fns';
import { es } from 'date-fns/locale';

const Reports = () => {
  const navigate = useNavigate();
  
  const [devices, setDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState('');
  const [reportType, setReportType] = useState('mileage');
  const [startDate, setStartDate] = useState(format(subDays(new Date(), 7), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);

  // Fetch devices
  useEffect(() => {
    const fetchDevices = async () => {
      try {
        const response = await devicesAPI.getAll();
        setDevices(response.data || []);
        if (response.data?.length > 0) {
          setSelectedDevice(response.data[0].imei);
        }
      } catch (err) {
        console.error('Error fetching devices:', err);
      }
    };
    fetchDevices();
  }, []);

  // Generate report
  const generateReport = async () => {
    if (!selectedDevice) return;
    
    setLoading(true);
    try {
      const beginTime = `${startDate} 00:00:00`;
      const endTimeStr = `${endDate} 23:59:59`;
      
      let response;
      switch (reportType) {
        case 'mileage':
          response = await reportsAPI.getMileage(selectedDevice, beginTime, endTimeStr);
          break;
        case 'trips':
          response = await reportsAPI.getTrips(selectedDevice, beginTime, endTimeStr);
          break;
        case 'alarms':
          response = await alarmsAPI.getAlarms(selectedDevice, beginTime, endTimeStr);
          break;
        default:
          response = { data: null };
      }
      
      setReportData(response.data);
    } catch (err) {
      console.error('Error generating report:', err);
      setReportData(null);
    } finally {
      setLoading(false);
    }
  };

  // Export to CSV
  const exportCSV = () => {
    if (!reportData) return;
    
    let csvContent = '';
    let data = Array.isArray(reportData) ? reportData : [reportData];
    
    if (data.length === 0) return;
    
    // Headers
    const headers = Object.keys(data[0]);
    csvContent += headers.join(',') + '\n';
    
    // Rows
    data.forEach(row => {
      csvContent += headers.map(h => `"${row[h] || ''}"`).join(',') + '\n';
    });
    
    // Download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `reporte_${reportType}_${selectedDevice}_${startDate}_${endDate}.csv`;
    link.click();
  };

  // Render report content
  const renderReport = () => {
    if (!reportData) return null;
    
    if (reportType === 'mileage') {
      return (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <div className="text-sm text-gray-500 mb-1">Kilometraje Total</div>
              <div className="text-2xl font-bold">{reportData.mileage || 0} km</div>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <div className="text-sm text-gray-500 mb-1">Tiempo en movimiento</div>
              <div className="text-2xl font-bold">{reportData.runningTime || '-'}</div>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <div className="text-sm text-gray-500 mb-1">Tiempo detenido</div>
              <div className="text-2xl font-bold">{reportData.stopTime || '-'}</div>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <div className="text-sm text-gray-500 mb-1">Velocidad máxima</div>
              <div className="text-2xl font-bold">{reportData.maxSpeed || 0} km/h</div>
            </div>
          </div>
        </div>
      );
    }
    
    if (reportType === 'trips') {
      const trips = Array.isArray(reportData) ? reportData : [];
      return (
        <div className="overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr>
                <th>Inicio</th>
                <th>Fin</th>
                <th>Duración</th>
                <th>Distancia</th>
                <th>Vel. Máx</th>
                <th>Dirección Inicio</th>
              </tr>
            </thead>
            <tbody>
              {trips.length > 0 ? trips.map((trip, i) => (
                <tr key={i}>
                  <td>{trip.startTime ? format(new Date(trip.startTime), 'dd/MM HH:mm', { locale: es }) : '-'}</td>
                  <td>{trip.endTime ? format(new Date(trip.endTime), 'dd/MM HH:mm', { locale: es }) : '-'}</td>
                  <td>{trip.duration || '-'}</td>
                  <td>{trip.mileage || 0} km</td>
                  <td>{trip.maxSpeed || 0} km/h</td>
                  <td className="max-w-[200px] truncate">{trip.startAddress || '-'}</td>
                </tr>
              )) : (
                <tr>
                  <td colSpan={6} className="text-center text-gray-500 py-8">
                    No hay viajes registrados en este período
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      );
    }
    
    if (reportType === 'alarms') {
      const alarms = Array.isArray(reportData) ? reportData : [];
      return (
        <div className="overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr>
                <th>Fecha/Hora</th>
                <th>Tipo</th>
                <th>Descripción</th>
                <th>Ubicación</th>
                <th>Velocidad</th>
              </tr>
            </thead>
            <tbody>
              {alarms.length > 0 ? alarms.map((alarm, i) => (
                <tr key={i} className={alarm.alarmLevel === 'high' ? 'alert-high' : 'alert-medium'}>
                  <td>{alarm.alarmTime ? format(new Date(alarm.alarmTime), 'dd/MM HH:mm', { locale: es }) : '-'}</td>
                  <td>
                    <span className="px-2 py-1 rounded bg-destructive/20 text-destructive text-xs font-medium">
                      {alarm.alarmTypeNameEn || alarm.alarmType || 'Alarma'}
                    </span>
                  </td>
                  <td>{alarm.alarmDesc || alarm.alarmContent || '-'}</td>
                  <td className="max-w-[200px] truncate">{alarm.address || '-'}</td>
                  <td>{alarm.speed || 0} km/h</td>
                </tr>
              )) : (
                <tr>
                  <td colSpan={5} className="text-center text-gray-500 py-8">
                    No hay alarmas registradas en este período
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      );
    }
    
    return null;
  };

  return (
    <div className="min-h-screen bg-gray-100" data-testid="reports-page">
      {/* Header */}
      <header className="h-16 border-b border-gray-200 flex items-center justify-between px-4 bg-white/95 backdrop-blur sticky top-0 z-10">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/')}
            className="p-2 rounded-lg hover:bg-gray-200 transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="font-bold flex items-center gap-2">
              <FileText size={20} className="text-primary" />
              Reportes
            </h1>
            <p className="text-xs text-gray-600">
              Genera reportes detallados de tu flota
            </p>
          </div>
        </div>
        
        {reportData && (
          <button
            onClick={exportCSV}
            className="h-9 px-4 rounded-lg bg-secondary text-secondary-foreground font-medium hover:bg-secondary/90 transition-colors flex items-center gap-2"
            data-testid="export-csv-btn"
          >
            <DownloadSimple size={18} /> Exportar CSV
          </button>
        )}
      </header>

      <main className="max-w-7xl mx-auto p-6 space-y-6">
        {/* Filters */}
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {/* Device Select */}
            <div>
              <label className="text-xs text-gray-600 font-medium mb-1 block">Dispositivo</label>
              <div className="relative">
                <select
                  value={selectedDevice}
                  onChange={(e) => setSelectedDevice(e.target.value)}
                  data-testid="device-select"
                  className="w-full h-10 px-3 pr-8 rounded-lg bg-white border border-gray-300 text-sm text-gray-800 appearance-none"
                >
                  {devices.map(d => (
                    <option key={d.imei} value={d.imei}>
                      {d.deviceName || d.imei}
                    </option>
                  ))}
                </select>
                <CaretDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-500" />
              </div>
            </div>

            {/* Report Type */}
            <div>
              <label className="text-xs text-gray-600 font-medium mb-1 block">Tipo de Reporte</label>
              <div className="relative">
                <select
                  value={reportType}
                  onChange={(e) => setReportType(e.target.value)}
                  data-testid="report-type-select"
                  className="w-full h-10 px-3 pr-8 rounded-lg bg-white border border-gray-300 text-sm text-gray-800 appearance-none"
                >
                  <option value="mileage">Kilometraje</option>
                  <option value="trips">Viajes</option>
                  <option value="alarms">Alarmas</option>
                </select>
                <CaretDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-500" />
              </div>
            </div>

            {/* Start Date */}
            <div>
              <label className="text-xs text-gray-600 font-medium mb-1 block">Fecha inicio</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                data-testid="start-date-input"
                className="w-full h-10 px-3 rounded-lg bg-white border border-gray-300 text-sm text-gray-800"
              />
            </div>

            {/* End Date */}
            <div>
              <label className="text-xs text-gray-600 font-medium mb-1 block">Fecha fin</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                data-testid="end-date-input"
                className="w-full h-10 px-3 rounded-lg bg-white border border-gray-300 text-sm text-gray-800"
              />
            </div>

            {/* Generate Button */}
            <div className="flex items-end">
              <button
                onClick={generateReport}
                disabled={loading || !selectedDevice}
                data-testid="generate-report-btn"
                className="w-full h-10 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
              >
                {loading ? (
                  <Circle size={18} className="spinner" />
                ) : (
                  <>
                    <FileText size={18} /> Generar
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Report Type Info */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button
            onClick={() => setReportType('mileage')}
            className={`p-4 rounded-xl border text-left transition-colors ${
              reportType === 'mileage' 
                ? 'border-primary bg-primary/10' 
                : 'border-gray-200 bg-white hover:bg-gray-100'
            }`}
          >
            <div className="flex items-center gap-3 mb-2">
              <div className={`p-2 rounded-lg ${reportType === 'mileage' ? 'bg-primary/20' : 'bg-gray-200'}`}>
                <Path size={20} className={reportType === 'mileage' ? 'text-primary' : ''} />
              </div>
              <span className="font-semibold">Kilometraje</span>
            </div>
            <p className="text-xs text-gray-600">
              Distancia recorrida, tiempo en movimiento y detenido
            </p>
          </button>
          
          <button
            onClick={() => setReportType('trips')}
            className={`p-4 rounded-xl border text-left transition-colors ${
              reportType === 'trips' 
                ? 'border-primary bg-primary/10' 
                : 'border-gray-200 bg-white hover:bg-gray-100'
            }`}
          >
            <div className="flex items-center gap-3 mb-2">
              <div className={`p-2 rounded-lg ${reportType === 'trips' ? 'bg-primary/20' : 'bg-gray-200'}`}>
                <Car size={20} className={reportType === 'trips' ? 'text-primary' : ''} />
              </div>
              <span className="font-semibold">Viajes</span>
            </div>
            <p className="text-xs text-gray-600">
              Lista detallada de todos los viajes realizados
            </p>
          </button>
          
          <button
            onClick={() => setReportType('alarms')}
            className={`p-4 rounded-xl border text-left transition-colors ${
              reportType === 'alarms' 
                ? 'border-primary bg-primary/10' 
                : 'border-gray-200 bg-white hover:bg-gray-100'
            }`}
          >
            <div className="flex items-center gap-3 mb-2">
              <div className={`p-2 rounded-lg ${reportType === 'alarms' ? 'bg-primary/20' : 'bg-gray-200'}`}>
                <Warning size={20} className={reportType === 'alarms' ? 'text-primary' : ''} />
              </div>
              <span className="font-semibold">Alarmas</span>
            </div>
            <p className="text-xs text-gray-600">
              Historial de alertas y eventos del dispositivo
            </p>
          </button>
        </div>

        {/* Report Content */}
        <div className="bg-white border border-gray-200 rounded-xl p-4 min-h-[300px]">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Circle size={32} className="spinner text-primary" />
            </div>
          ) : reportData ? (
            renderReport()
          ) : (
            <div className="flex flex-col items-center justify-center h-64 text-gray-500">
              <FileText size={48} className="mb-4" />
              <p>Selecciona los filtros y genera un reporte</p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default Reports;
